import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Users, Award, ArrowRight, Film, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { organizationInfo, founders, teamMembers } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Aloha Spirit',
      description: 'We approach everything we do with love, compassion, and the true spirit of Hawaii.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Building a supportive ohana where no one faces cancer alone.'
    },
    {
      icon: Award,
      title: 'Holistic Care',
      description: 'Embracing both traditional and integrative approaches to cancer support.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">About Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Our Story & Mission
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              The Aloha Cancer Project was founded with a simple yet powerful vision: to ensure that 
              no one in Hawaii faces cancer alone. We are a resource hub, support network, and advocate 
              for cancer patients, survivors, and their caregivers.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="w-16 h-16 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#E07A5F]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2D2A32] mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                {organizationInfo.mission}
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Provide comprehensive cancer education and resources</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Connect families with healthcare providers and support services</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Advocate for improved cancer care policies in Hawaii</span>
                </li>
              </ul>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="w-16 h-16 bg-[#5D4E6D]/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#5D4E6D]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2D2A32] mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed">
                {organizationInfo.vision}
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#5D4E6D] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Be Hawaii's most trusted cancer support resource</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#5D4E6D] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Create lasting positive impact on families throughout the islands</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#5D4E6D] rounded-full mt-2 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Build a community where hope and healing flourish</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">What We Believe</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Our Core Values
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <Card
                  key={value.title}
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
                >
                  <CardContent className="p-8">
                    <div className="w-20 h-20 bg-[#F5F0EB] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E07A5F] transition-colors duration-300">
                      <IconComponent className="w-10 h-10 text-[#5D4E6D] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2D2A32] mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1515688154098-1c039ba8d1b8?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8cGx1bWVyaWF8ZW58MHx8MHx8fDA%3D"
                  alt="Hawaiian plumeria flowers"
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#E07A5F]/20 rounded-full blur-2xl" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Approach</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2A32] mt-4 mb-6">
                Holistic & Integrative Support
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We believe in a comprehensive approach to cancer care that goes beyond traditional medicine. 
                Our organization connects patients and families with a wide network of resources including:
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-[#E07A5F] font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2D2A32]">Traditional Medicine</h4>
                    <p className="text-gray-600 text-sm">Connecting with oncologists and conventional treatment resources</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-[#E07A5F] font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2D2A32]">Alternative & Integrative Care</h4>
                    <p className="text-gray-600 text-sm">Supporting complementary treatments and holistic wellness</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-[#E07A5F] font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2D2A32]">Nutritional Support</h4>
                    <p className="text-gray-600 text-sm">Access to nutritionists and healthy food resources</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-[#E07A5F] font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2D2A32]">Mental Health & Wellness</h4>
                    <p className="text-gray-600 text-sm">Counseling, support groups, and emotional care</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Founders</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Meet Our Founders
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              The visionary leaders who started the Aloha Cancer Project with a mission to support Hawaii's cancer community.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {founders.map((founder) => (
              <Card
                key={founder.id}
                data-testid="founders-card"
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group"
              >
                <div className="h-3 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                    <img
                      src={founder.image}
                      alt={founder.names.join(' and ')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-10 flex flex-col justify-center">
                    <p className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider mb-3">
                      {founder.role}
                    </p>
                    <div className="space-y-2 mb-6">
                      {founder.names.map((name) => (
                        <h3 key={name} className="text-3xl font-bold text-[#2D2A32]">
                          {name}
                        </h3>
                      ))}
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      The visionary leaders behind the Aloha Cancer Project, united by a shared mission to
                      bring hope, support, and the spirit of Aloha to Hawaii's cancer community.
                    </p>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>

          {/* Featured Story: SuperCW Documentary */}
          <div className="max-w-5xl mx-auto mt-16">
            <div className="text-center mb-10">
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Their Story</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#2D2A32] mt-3">
                The Adventures of SuperCW
              </h3>
            </div>

            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-2 aspect-square md:aspect-auto overflow-hidden bg-[#2D2A32]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_6ed424f7-dd20-4020-85d3-049c99db07b8/artifacts/ebmupb60_Screenshot%202026-06-02%20102607.png"
                    alt="The Adventures of SuperCW documentary featuring Christa Wittmier"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="md:col-span-3 p-8 sm:p-10 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Badge className="bg-[#E07A5F]/10 text-[#E07A5F] hover:bg-[#E07A5F]/20">
                      <Film className="w-3 h-3 mr-1" />
                      Documentary
                    </Badge>
                    <Badge variant="outline" className="border-[#5D4E6D]/30 text-[#5D4E6D]">
                      2019 · HIFF Spring Hits
                    </Badge>
                  </div>
                  <h4 className="text-2xl font-bold text-[#2D2A32] mb-3">
                    A Beacon of Positive Energy
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Christa Wittmier, aka <span className="font-semibold text-[#2D2A32]">#SuperCW</span>,
                    was one of the most prominent and photographed nightlife columnists, DJs and
                    personalities in Honolulu for over a decade — a champion of artists, creatives,
                    and anyone who imbued the Aloha spirit.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Directed by Gerard Elmore, <em>The Adventures of SuperCW (*Cancer Happens)</em>
                    chronicles her journey through breast cancer — wearing her heart on her sleeve,
                    promoting wellness and self-care, and showing the highs and lows of a quirky
                    young metastatic warrior who fought to make an impact and live life to its fullest.
                  </p>
                  <div>
                    <Button
                      asChild
                      data-testid="supercw-article-link"
                      className="bg-[#5D4E6D] hover:bg-[#4A3D58] text-white rounded-full px-6 transition-all duration-300"
                    >
                      <a
                        href="https://www.staradvertiser.com/2019/03/20/features/hiff-to-feature-documentary-on-super-cw/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read the Star-Advertiser Feature
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Team</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Meet Our Ohana
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Our dedicated team of volunteers, board members, and partners work together to support Hawaii's cancer community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card
                key={member.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="h-3 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#F5F0EB] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-10 h-10 text-[#5D4E6D]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2D2A32] mb-2">{member.name}</h3>
                  <p className="text-[#E07A5F] font-medium text-sm mb-4">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5D4E6D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Join Our Mission
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            Whether you're looking for support or want to help others, we welcome you to be part of our ohana.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-8 transition-all duration-300"
            >
              <Link to="/get-involved">
                Get Involved
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#5D4E6D] rounded-full px-8 transition-all duration-300 bg-transparent"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
